/**
 * Keep Alive Instance API
 * 
 * This module handles API calls for keeping instances alive using AWS Step Functions.
 * It provides a clean interface for the frontend to interact with the keep-alive functionality.
 */

import ApiConfig from "../services/ApiConfig";
import axios from "axios";

/**
 * Environment mapping from short codes to full environment names
 */
const environmentMapping = {
    'dcx': 'dazndcxlive',
    'dca': 'dazndcalive', 
    'dcb': 'dazndcblive',
    'dcc': 'dazndcclive',
    'dcd': 'dazndcdlive',
    'dco': 'dazndcolive',
    'dci': 'dazncilive',
    'dta': 'dazntestalphalive'
};

/**
 * Keep an instance alive by invoking the appropriate Step Function
 * 
 * @param {string} eventId - The event ID
 * @param {string} environment - The environment (short code or full name)
 * @param {string[]} instanceIds - Array of instance IDs to keep alive
 * @param {string} ticketId - The ticket ID for tracking
 * @returns {Promise<Object>} Response object with success/error information
 */
const keepAliveInstanceApi = async (eventId, environment, instanceIds, ticketId) => {
    try {
        // Validate inputs
        if (!eventId || !environment || !instanceIds || !Array.isArray(instanceIds) || instanceIds.length === 0) {
            throw new Error("Invalid parameters: eventId, environment, and instanceIds are required");
        }

        // Map short environment codes to full names
        const fullEnvironmentName = environmentMapping[environment] || environment;
        
        // Get API configuration
        var config = await ApiConfig();
        config.method = "post";
        config.url += "/keep_alive_instance";
        
        // Prepare request payload
        config.data = {
            environment: fullEnvironmentName,
            eventId: eventId,
            originSelectionMode: "BY_INSTANCE_ID",
            originInstanceIdsToKeep: instanceIds,
            ticketIdentifier: ticketId
        };
        
        // Make the API call
        return await axios(config)
            .then(function (response) {
                return response.data;
            })
            .catch(function (error) {
                // Handle different types of errors
                if (error.response) {
                    const status = error.response.status;
                    const data = error.response.data;
                    
                    // Handle specific error cases
                    if (status === 409 && data && data.alreadyExists) {
                        return {
                            message: "Keep Alive already done for this instance",
                            alreadyExists: true,
                            executionArn: null
                        };
                    } else if (status === 403) {
                        throw new Error("Access denied to Step Function. Please check IAM permissions or contact the administrator.");
                    } else if (status === 404) {
                        throw new Error("Cross-account role not found. Please contact the administrator.");
                    } else if (status === 400) {
                        const message = data?.message || data?.error || "Invalid request parameters";
                        throw new Error(message);
                    } else if (data && data.message) {
                        throw new Error(data.message);
                    } else {
                        throw new Error(`Server error (${status}). Please try again or contact support.`);
                    }
                } else if (error.message) {
                    throw new Error(error.message);
                } else {
                    throw new Error("Network error. Please check your connection and try again.");
                }
            });
    } catch (error) {
        console.error("Error in keepAliveInstanceApi:", error);
        throw error;
    }
};

export default keepAliveInstanceApi;
